import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Tabs, Tab, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
} from '@mui/material'
import {
  Add, AcUnit, Warning, CheckCircle, Verified, AssignmentTurnedIn,
} from '@mui/icons-material'
import {
  useTemperatureProfiles, useUpsertTemperatureProfile,
  useTemperatureExcursions, useAcknowledgeExcursion,
  TemperatureProfile,
} from '../../hooks/useWave2'
import { useDevices } from '../../hooks/useDevices'

export default function ColdChainPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <AcUnit sx={{ color: '#0ea5e9' }} />
        <Typography variant="h5" fontWeight={700} color="#0f172a">
          {t('coldchain.title', 'Cold Chain Compliance')}
        </Typography>
      </Stack>
      <Typography variant="caption" color="#64748b" sx={{ mb: 3, display: 'block' }}>
        {t('coldchain.subtitle', 'Temperature profiles, excursion alerts, GDP/HACCP compliance certificates.')}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t('coldchain.profiles', 'Profiles')} />
        <Tab label={t('coldchain.excursions', 'Excursions')} />
      </Tabs>

      {tab === 0 && <ProfilesTab />}
      {tab === 1 && <ExcursionsTab />}
    </Box>
  )
}

function ProfilesTab() {
  const { t } = useTranslation()
  const profiles = useTemperatureProfiles()
  const devices = useDevices()
  const upsert = useUpsertTemperatureProfile()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<Partial<TemperatureProfile>>({
    zone_label: 'main', target_min_c: 2, target_max_c: 8, hysteresis_c: 0.5,
    min_duration_sec: 120, regulation: 'gdp', is_active: true,
  })

  return (
    <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography fontWeight={700}>{t('coldchain.profiles', 'Temperature Profiles')}</Typography>
        <Button startIcon={<Add />} onClick={() => setOpen(true)}>{t('coldchain.new_profile', 'New profile')}</Button>
      </Stack>

      {profiles.isLoading ? (
        <CircularProgress size={20} />
      ) : (profiles.data?.length ?? 0) === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
          {t('coldchain.no_profiles', 'No profiles configured. Add one per device + zone.')}
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('coldchain.device', 'Device')}</TableCell>
              <TableCell>{t('coldchain.zone', 'Zone')}</TableCell>
              <TableCell>{t('coldchain.range', 'Target range (°C)')}</TableCell>
              <TableCell>{t('coldchain.regulation', 'Regulation')}</TableCell>
              <TableCell align="right">{t('common.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(profiles.data ?? []).map((p) => {
              const dev = devices.data?.find((d) => d.id === p.device_id)
              return (
                <TableRow key={p.id} hover>
                  <TableCell>{dev?.name ?? p.device_id.slice(0, 8)}</TableCell>
                  <TableCell><Chip size="small" label={p.zone_label} /></TableCell>
                  <TableCell>
                    <Typography fontFamily="monospace">
                      {p.target_min_c.toFixed(1)} – {p.target_max_c.toFixed(1)} ±{p.hysteresis_c}
                    </Typography>
                  </TableCell>
                  <TableCell>{p.regulation ? <Chip size="small" label={p.regulation.toUpperCase()} icon={<Verified fontSize="small" />} /> : '—'}</TableCell>
                  <TableCell align="right">
                    {p.is_active ? <CheckCircle color="success" fontSize="small" /> : <Chip size="small" label="off" />}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('coldchain.new_profile', 'New temperature profile')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={t('coldchain.device', 'Device')} value={form.device_id ?? ''}
                       onChange={(e) => setForm({ ...form, device_id: e.target.value })} required fullWidth>
              {(devices.data ?? []).map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
            <TextField label={t('coldchain.zone_label', 'Zone label')} value={form.zone_label ?? 'main'}
                       onChange={(e) => setForm({ ...form, zone_label: e.target.value })} fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label={t('coldchain.min_c', 'Min °C')} type="number" value={form.target_min_c ?? 2}
                         onChange={(e) => setForm({ ...form, target_min_c: Number(e.target.value) })} fullWidth />
              <TextField label={t('coldchain.max_c', 'Max °C')} type="number" value={form.target_max_c ?? 8}
                         onChange={(e) => setForm({ ...form, target_max_c: Number(e.target.value) })} fullWidth />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField label={t('coldchain.hysteresis', 'Hysteresis °C')} type="number" value={form.hysteresis_c ?? 0.5}
                         onChange={(e) => setForm({ ...form, hysteresis_c: Number(e.target.value) })} fullWidth />
              <TextField label={t('coldchain.min_duration', 'Min duration (s)')} type="number" value={form.min_duration_sec ?? 120}
                         onChange={(e) => setForm({ ...form, min_duration_sec: Number(e.target.value) })} fullWidth />
            </Stack>
            <TextField select label={t('coldchain.regulation', 'Regulation')} value={form.regulation ?? ''}
                       onChange={(e) => setForm({ ...form, regulation: e.target.value })} fullWidth>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="gdp">GDP (Pharma)</MenuItem>
              <MenuItem value="haccp">HACCP (Food)</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" disabled={!form.device_id || (form.target_min_c ?? 0) >= (form.target_max_c ?? 0)}
                  onClick={async () => { await upsert.mutateAsync(form); setOpen(false) }}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

function ExcursionsTab() {
  const { t } = useTranslation()
  const [unackOnly, setUnackOnly] = useState(true)
  const excursions = useTemperatureExcursions(unackOnly)
  const ack = useAcknowledgeExcursion()
  const devices = useDevices()

  return (
    <Card sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Warning color="warning" />
          <Typography fontWeight={700}>
            {t('coldchain.excursions', 'Temperature Excursions')}
          </Typography>
        </Stack>
        <Button size="small" variant={unackOnly ? 'contained' : 'outlined'}
                onClick={() => setUnackOnly((v) => !v)}>
          {unackOnly ? t('coldchain.unack_only', 'Unacknowledged only') : t('coldchain.show_all', 'All')}
        </Button>
      </Stack>

      {excursions.isLoading ? (
        <CircularProgress size={20} />
      ) : (excursions.data?.length ?? 0) === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#10b981' }}>
          <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
          <Typography>{t('coldchain.all_clear', 'All clear — no excursions in range.')}</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('coldchain.started', 'Started')}</TableCell>
              <TableCell>{t('coldchain.device', 'Device')}</TableCell>
              <TableCell>{t('coldchain.zone', 'Zone')}</TableCell>
              <TableCell>{t('coldchain.peak', 'Peak (°C)')}</TableCell>
              <TableCell>{t('coldchain.duration', 'Duration')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(excursions.data ?? []).map((e) => {
              const dev = devices.data?.find((d) => d.id === e.device_id)
              const dirIcon = e.direction === 'above' ? '↑' : '↓'
              const dirColor = e.direction === 'above' ? '#ef4444' : '#0ea5e9'
              return (
                <TableRow key={e.id} hover>
                  <TableCell>{new Date(e.started_at).toLocaleString()}</TableCell>
                  <TableCell>{dev?.name ?? e.device_id.slice(0, 8)}</TableCell>
                  <TableCell><Chip size="small" label={e.zone_label} /></TableCell>
                  <TableCell sx={{ color: dirColor, fontWeight: 700 }}>
                    {dirIcon} {e.peak_value_c.toFixed(1)}°
                  </TableCell>
                  <TableCell>{e.duration_sec ? `${Math.round(e.duration_sec / 60)} min` : '—'}</TableCell>
                  <TableCell align="right">
                    {e.acknowledged ? (
                      <Tooltip title={e.note || 'Acknowledged'}>
                        <Chip size="small" icon={<AssignmentTurnedIn fontSize="small" />} label="Ack" color="success" />
                      </Tooltip>
                    ) : (
                      <Button size="small" startIcon={<AssignmentTurnedIn fontSize="small" />}
                              onClick={() => ack.mutate({ id: e.id })} disabled={ack.isPending}>
                        {t('coldchain.ack', 'Acknowledge')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  )
}
